import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useNoteListStore } from 'src/stores/noteList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupNoteList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const noteListStore = useNoteListStore();

	void noteListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});

	void noteListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchNoteList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchNoteList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useNoteListStore> = useNoteListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetOpenNoteList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetMineNoteList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetAllNoteList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchOpenNoteList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchMineNoteList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchAllNoteList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('noteList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchNoteList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 1.7 (User-Entscheidung 2026-07-24): Basis-Identifier der drei Sub-Listen-Scroll-Positionen
// (Offen/Meine/Alle) — muss `NoteList.vue`s `virtual-list-id="moduleActivityListScrollerNotes"`
// spiegeln (dort mit Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein
// zentraler Export vorhanden, daher hier dieselbe Literal-Konstante wie in NoteList.vue.
const NOTE_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerNotes';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen
// bereits vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im
// Layout-Hook filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters/selectTheSharedQuery
// (FilterComponentActions.ts) für 'noteList' abgeleitet, OHNE deren router.replace/refreshTheList:
// Box-/Benutzer-Filter, Datumsfilter (+ Zeitraum-Preset), gewählte Shared-Query, Such-/Hidden-Suchbegriff,
// Mehrfachauswahl, Scroll-Positionen der drei Sub-Listen, aktive Ansicht. `noteList` hat keine
// Richtungs-Filter (isDirectionRoute() schließt es aus) — kein Reset-Ziel dafür.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetNoteListModuleTabSettings = (): void => {
	const noteListStore = useNoteListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	noteListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	noteListStore.setFilteredUsersAction({ filterUsers: [] });
	noteListStore.setDateFilterAction({ dateFilter: today() });
	noteListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	noteListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });

	filterStore.setSearchTerm({ module: 'noteList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'noteList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'noteList' });
	genericActivityListStore.resetScrollPositions(NOTE_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({ activeView: MODULE_LIST_TYPE.OPEN, genericListIdentifier: 'noteList' });
};

registerModuleTabSettingsReset('noteList', resetNoteListModuleTabSettings);

const deleteTheSelectedNoteAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const noteListStore: ReturnType<typeof useNoteListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useNoteListStore();

		noteListStore.deleteSelectedAggregateIds({
			aggregateIds: queryParameters.aggregateIds,
		});
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'noteList',
	});
};

const setTheStateForTheSelectedNoteAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const noteListStore: ReturnType<typeof useNoteListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useNoteListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				noteListStore.setTheNoteStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				noteListStore.setTheNoteStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				noteListStore.setTheNoteStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'noteList',
	});
};

export { deleteTheSelectedNoteAggregateIds, fetchNoteList, setTheStateForTheSelectedNoteAggregateIds, warmupNoteList };
